import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import '../App.css'; // Ensure the correct path to App.css
import ErrorMessage from '../common/components/ErrorMessage'; // Import the ErrorMessage component
import SuccessMessage from '../common/components/SuccessMessage';
import Product from '../common/components/Product';
import Post from '../services/postService'; // Import the Post function
import AuthData from "../common/AuthData";
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import YesNoPopup from '../common/components/YesNoPopup';
import Delete from '../services/deleteService'; // Import the Delete service
import { useParams } from 'react-router-dom'; // Import useParams
import Fetch from '../services/fetchService';
import { PRODUCTS_API } from '../common/ResourcePaths'; // Import the constants
import DragDropImageUpload from '../common/components/DragDropImageUpload';
import Feedback from '../common/components/Feedback';

function NewProduct({ onSuccess }) {
    const { id: productId } = useParams(); // Extract product ID from the URL
    const navigate = useNavigate(); // Initialize navigate hook
    const minImgSize = 220;

    const [newItem, setNewItem] = useState(() => {
        return { quantity: 0 };
    });
    const [originalPrice, setOriginalPrice] = useState(0); // Store original price before discount
    const [imagePreview, setImagePreview] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null); // Add state for error message
    const [loader, setLoader] = useState(false); // State to track loading
    const [successTimer, setSuccessTimer] = useState(10);
    const [success, setSuccess] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [editButton, setEditButton] = useState(false);
    const [deleteButton, setDeleteButton] = useState(false); // State to track delete confirmation
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showDescriptionPopup, setShowDescriptionPopup] = useState(false); // State for description popup
    const [tempDescription, setTempDescription] = useState(''); // Temporary description for popup

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoader(true); // Set loading state to true before fetching data
                const authData = AuthData.getAuthData();
                const url = `${authData.appFunctionUrl}${PRODUCTS_API}/${productId}?code=${authData.azureFunctionKey}`;
                const productData = await Fetch(url, authData.clientId);
                if (productData) {
                    setNewItem(productData); // Set the fetched product data
                    // Initialize original price - if it's a promotion, calculate original from current price and discount
                    if (productData.isPromotion && productData.promotionValue) {
                        const originalPriceCalculated = productData.price / (1 - productData.promotionValue / 100);
                        setOriginalPrice(parseFloat(originalPriceCalculated.toFixed(2)));
                    } else {
                        setOriginalPrice(productData.price || 0);
                    }
                    setEditButton(false); // Enable edit button
                    setDeleteButton(true); // Enable delete button
                }
            } catch (error) {
                console.error("Error fetching product:", error);
            } finally {
                setLoader(false); // Reset loading state after fetching
            }
        };

        if (productId) {
            fetchProduct(); // Fetch product only if productId exists
        } else {
            // Reset form for new product
            setNewItem({ quantity: 0 });
            setOriginalPrice(0);
            setImagePreview(null);
            setEditButton(false);
            setDeleteButton(false);
            setErrorMessage(null);
            setSuccess(false);
        }
    }, [productId]);

    const validateFields = () => {
        if (!newItem.title || !newItem.price || !newItem.category) {
            setErrorMessage('Title, Price, and Category are required. Please fill out all required fields.');
            return false;
        }

        setErrorMessage(''); // Clear error message if validation passes
        return true;
    };

    const handleImageUpload = (file, errorMsg = null) => {
        if (errorMsg) {
            setErrorMessage(errorMsg);
            return;
        }
        
        if (file) {
            const reader = new FileReader();
            const img = new Image();

            reader.onload = () => {
                img.src = reader.result;
                img.onload = () => {
                    if (img.width >= minImgSize && img.height >= minImgSize) {
                        setImagePreview(reader.result);
                        const updatedNewItem = { ...newItem, src: reader.result, image: null };
                        setNewItem(updatedNewItem);
                        setErrorMessage(''); // Clear error message
                    } else {
                        setErrorMessage(`Image dimensions must be at least ${minImgSize}px by ${minImgSize}px.`);
                    }
                };
            };

            reader.readAsDataURL(file);
        }
    };

    const handleImageRemove = () => {
        setImagePreview(null);
        setNewItem({ ...newItem, src: null, image: null });
        setErrorMessage(''); // Clear any error messages
    };

    const remove = async () => {
        try {
            const id = newItem.id; // Get the ID of the item to be deleted
            const authData = AuthData.getAuthData();
            const url = `${authData.appFunctionUrl}${PRODUCTS_API}/${id}?code=${authData.azureFunctionKey}`; // Use the constant here
            await Delete(url, authData.clientId); 
            navigate('/items'); // Navigate to the product list after deletion
        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    const handelDelete = () => {
        setShowPopup(true);
    }

    const handleYes = async () => {
        setShowPopup(false);
        setLoader(true); // Start loader
        await remove(); // Perform the delete operation
        navigate('/items'); // Navigate to the product list
    };

    const handleNo = () => {
        setShowPopup(false);
    };

    const handlePromotionChange = (e) => {
        const checked = e.target.checked;
        if (checked) {
            // Store original price when enabling promotion
            setOriginalPrice(newItem.price || 0);
            setNewItem({ ...newItem, isPromotion: true, promotionValue: null });
        } else {
            // Restore original price when disabling promotion
            setNewItem({ ...newItem, isPromotion: false, promotionValue: null, price: originalPrice });
        }
    };

    const handleSubmit = async (isDraft) => {
        if (!validateFields()) return; // Stop if validation fails

        setLoader(true); // Start loader
        try {
            const authData = AuthData.getAuthData();
            // Determine the URL based on whether the product has an ID
            const url = newItem.id 
                ? `${authData.appFunctionUrl}${PRODUCTS_API}?code=${authData.azureFunctionKey}` 
                : `${authData.appFunctionUrl}${PRODUCTS_API}/new?code=${authData.azureFunctionKey}`;
            
            newItem.isDraft = isDraft; // Set isDraft property based on the button clicked
            const response = await Post(url, authData.clientId, newItem);
            if (response.statusCode === 201 || response.StatusCode === 200) { // Check if the response status code is 201
                setErrorMessage(null); // Clear error message on success
                setSuccess(true); // Show success message
                setSuccessTimer(10); // Reset timer to 10 seconds

                // Call onSuccess callback if provided, otherwise navigate
                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate('/items');
                } 
            } else {
                console.error('Failed to submit product:', response.StatusCode);
                setErrorMessage('Failed to submit product. Please try again.'); // Set error message
            }
        } catch (error) {
            console.error('Error submitting product:', error);
            setErrorMessage('An error occurred while submitting the product. Please try again.'); // Set error message
        } finally {
            setLoader(false); // Stop loader
        }
    };

    return (
        <div className="container" style={{ position: 'relative' }}>
                {loader && <RelativeSvgLoader />}
                <div className='panel' style={{ opacity: loader ? 0.3 : 1 }}>
                    {errorMessage && ( // Conditionally render the error message
                        <ErrorMessage message={errorMessage} />
                    )}
                    {success && ( // Conditionally render the success message
                        <SuccessMessage message={`Data saved successfully! (${successTimer}s)`} />
                    )}
            <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                padding: "20px" 
            }}>
                <Product item={newItem}
                        handleDelete={handelDelete}
                        showEdit={editButton}
                        showDelete={deleteButton}/>
            </div>
            <div className="form-container">
                <form className="form" onSubmit={(e) => e.preventDefault()}>
                    <div className="input-fields">
                        <div className="input-group">
                            <label className="label" htmlFor="title">Title</label>
                            <input
                                className="input-field"
                                type="text"
                                id="title"
                                value={newItem.title || ''} // Bind the title to the input field
                                onChange={(e) => {
                                    if (e.target.value.length <= 30) { // Validate max 20 characters
                                        setNewItem({ ...newItem, title: e.target.value }); // Update the title in newItem
                                    }
                                }}
                            />
                            {newItem.title && newItem.title.length > 30 && (
                                <ErrorMessage message="Title must be less than 20 characters." /> // Show error message if title exceeds 20 characters
                            )}
                        </div>
                        <div className="input-group">
                            <label className="label" htmlFor="text">Short text below</label>
                            <input
                                className="input-field"
                                type="text"
                                id="text"
                                name="text"
                                value={newItem.text || ''} // Bind the text to the input field
                                onChange={(e) => {
                                    if (e.target.value.length <= 50) { // Validate max 50 characters
                                        setNewItem({ ...newItem, text: e.target.value }); // Update the text in newItem
                                    }
                                }}
                            />
                            {newItem.text && newItem.text.length > 50 && (
                                <ErrorMessage message="Short text must be less than 50 characters." /> // Show error message if text exceeds 50 characters
                            )}
                        </div>

                        <div className="input-group">
                            <label className="label" htmlFor="price">Price</label>
                            <input
                                className="input-field"
                                type="number"
                                id="price"
                                name="price"
                                min="0"
                                step="0.01" // Allows float values
                                value={newItem.price || ''} // Bind the price to the input field
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const parsed = raw === '' ? '' : parseFloat(raw);
                                    const newPrice = parsed === '' || isNaN(parsed) ? '' : Math.max(0, parsed);
                                    setNewItem({ ...newItem, price: newPrice });
                                    // Update original price if not in promotion mode
                                    if (!newItem.isPromotion && newPrice !== '') {
                                        setOriginalPrice(newPrice);
                                    }
                                }} // Update the price in newItem
                            />
                        </div>

                        <div className="input-group">
                            <label className="label" htmlFor="quantity">Quantity</label>
                            <input
                                className="input-field"
                                type="number"
                                id="quantity"
                                name="quantity"
                                min="0"
                                step="1"
                                value={newItem.quantity === null ? '' : newItem.quantity} // Show empty if quantity is null
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const parsed = raw === '' ? null : parseInt(raw, 10);
                                    const newQuantity = parsed === null || isNaN(parsed) ? null : Math.max(0, parsed);
                                    setNewItem({ ...newItem, quantity: newQuantity });
                                }} // Update the quantity in newItem, preventing negative values
                                disabled={newItem.quantity === null} // Disable if quantity is null
                            />
                        </div>
                        <div className="input-group">
                            <label className="label" htmlFor="quantityCheckbox">Unlimited Quantity</label>
                            <label className="custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="quantityCheckbox"
                                    name="quantityCheckbox"
                                    checked={newItem.quantity === null}
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        if (isChecked) {
                                            setNewItem({ ...newItem, quantity: null }); // Set quantity to null when checked
                                        } else {
                                            setNewItem({ ...newItem, quantity: 1 }); // Set default quantity to 1 when unchecked
                                        }
                                    }}
                                />
                                <span className="checkbox-mark"></span>
                            </label>
                        </div>

                        <div className="input-group">
                            <label className="label" htmlFor="category">Category</label>
                            <input
                                className="input-field"
                                type="text"
                                id="category"
                                name="category"
                                title="Enter the product category"
                                value={newItem.category || ''} // Bind the weight to the input field
                                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} // Update the weight in newItem
                            />
                        </div>

                        <div className="input-group">
                            <label className="label" htmlFor="promotionCheckbox">Promotion</label>
                            <label className="custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="promotionCheckbox"
                                    name="promotionCheckbox"
                                    checked={newItem.isPromotion || false}
                                    onChange={handlePromotionChange}
                                />
                                <span className="checkbox-mark"></span>
                            </label>
                        </div>

                        {newItem.isPromotion && (
                            <div className="input-group">
                                <label className="label" htmlFor="discountPercentage">Discount (%)</label>
                                <input
                                    className="input-field"
                                    type="number"
                                    id="discountPercentage"
                                    name="discountPercentage"
                                    min="1"
                                    max="99"
                                    step="1"
                                    title="Enter discount percentage (1-99)"
                                    value={newItem.promotionValue || ''} 
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (inputValue === '') {
                                            // Clear promotion value and restore original price
                                            setNewItem({ ...newItem, promotionValue: null, price: originalPrice });
                                        } else {
                                            const value = parseInt(inputValue, 10);
                                            if (!isNaN(value) && value >= 1 && value <= 99) {
                                                // Calculate discounted price
                                                const discountedPrice = originalPrice * (1 - value / 100);
                                                setNewItem({ ...newItem, promotionValue: value, price: parseFloat(discountedPrice.toFixed(2)) });
                                            }
                                        }
                                    }}
                                />
                            </div>
                        )}

                        {/* Add Description Button */}
                        <div className="input-group">
                            <button 
                                type="button" 
                                className={`description-button ${newItem.description && newItem.description.trim() !== '' ? 'has-description' : ''}`}
                                onClick={() => {
                                    setTempDescription(newItem.description || '');
                                    setShowDescriptionPopup(true);
                                }}
                                style={{ marginTop: '10px' }}
                            >
                                {newItem.description && newItem.description.trim() !== '' ? (
                                    <>
                                        <span className="checkmark">✓</span>
                                        Description
                                    </>
                                ) : (
                                    'Description'
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Submit and Save as Draft Buttons */}
                    <div className="form-footer">
                        <button 
                            type="button" 
                            className='submit-button' 
                            onClick={() => setShowConfirmation(true)}>Publish</button>
                        <button 
                            type="button" 
                            className='save-draft-button' 
                            style={{ marginLeft: '10px' }}
                            onClick={() => handleSubmit(true)}>Save as Draft</button>
                    </div>
                </form>

                <DragDropImageUpload
                    onImageUpload={handleImageUpload}
                    imagePreview={imagePreview}
                    onImageRemove={handleImageRemove}
                    minSize={minImgSize}
                    placeholder="Image preview will appear here"
                />
            </div>
            </div>
            
            {/* YesNoComponent for confirmation */}
            {showConfirmation && (
                <YesNoPopup
                    message="This product will be published directly and visible on the site. Do you want to proceed?"
                    handleYes={() => {
                        handleSubmit(false);
                        setShowConfirmation(false);
                    }}
                    handleNo={() => setShowConfirmation(false)}
                />
            )}
            
            {showPopup && (
                <YesNoPopup
                    message="Are you sure you want to delete this item?"
                    handleYes={handleYes}
                    handleNo={handleNo} />
            )}
            
            {showDescriptionPopup && (
                <div className="popup-overlay">
                    <div className="description-popup">
                        <div className="popup-header">
                            <h3>Description</h3>
                            <button 
                                className="close-button"
                                onClick={() => setShowDescriptionPopup(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="popup-content">
                            <textarea
                                id="descriptionTextarea"
                                className="description-textarea"
                                rows={6}
                                placeholder="Enter product description here..."
                                value={tempDescription}
                                onChange={(e) => setTempDescription(e.target.value)}
                                maxLength={1000}
                            />
                            <div className="character-count">
                                {tempDescription.length}/1000 characters
                            </div>
                        </div>
                        <div className="popup-footer">
                            <button 
                                type="button" 
                                className="clear-data-button"
                                onClick={() => setTempDescription('')}
                            >
                                Clear
                            </button>
                            <button 
                                type="button" 
                                className="submit-button"
                                onClick={() => {
                                    setNewItem({ ...newItem, description: tempDescription });
                                    setShowDescriptionPopup(false);
                                }}
                            >
                                Save
                            </button>
                            <button 
                                type="button" 
                                className="save-draft-button"
                                style={{ marginLeft: '10px' }}
                                onClick={() => setShowDescriptionPopup(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Feedback system available on add/edit product page */}
            <Feedback />
        </div>
    );
}

export default NewProduct;